/**
 * Form pro B — per-field validation rules, evaluated client-side for inline
 * errors. Mirrors the backend `hub.apps.forms.validate_field_value` (the
 * server is the backstop); keeping the logic identical means a value the UI
 * accepts also passes the submit. Pure + unit-tested.
 */

export interface FieldValidation {
  /** A regex the value must FULLY match. */
  pattern?: string | null;
  min_length?: number | null;
  max_length?: number | null;
  /** Numeric bounds (the value is parsed as a number). */
  min?: number | null;
  max?: number | null;
  /** Override the default message for any failed rule. */
  message?: string | null;
}

/** Drop a trailing `.0` so an integer bound reads cleanly in a message. */
function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

/**
 * Validate `value` against `validation`, returning an error message or null.
 * Empty values are skipped (required-ness is a separate check), so an optional
 * field with rules only validates when filled.
 */
export function validateFieldValue(
  label: string,
  validation: FieldValidation | null | undefined,
  value: unknown,
): string | null {
  if (!validation) return null;
  if (value === null || value === undefined || value === "") return null;
  const s = String(value);
  const custom = validation.message?.trim() || null;
  const fail = (def: string) => custom || def;

  if (validation.pattern) {
    let re: RegExp | null = null;
    try {
      re = new RegExp(`^(?:${validation.pattern})$`);
    } catch {
      re = null; // a broken regex never blocks input
    }
    if (re && !re.test(s)) return fail(`${label} has an invalid format`);
  }
  const minLen = validation.min_length;
  if (minLen != null && s.length < minLen) {
    return fail(`${label} must be at least ${minLen} characters`);
  }
  const maxLen = validation.max_length;
  if (maxLen != null && s.length > maxLen) {
    return fail(`${label} must be at most ${maxLen} characters`);
  }
  const { min, max } = validation;
  if (min != null || max != null) {
    const num = Number(s);
    if (Number.isNaN(num)) return fail(`${label} must be a number`);
    if (min != null && num < min) return fail(`${label} must be ≥ ${fmtNum(min)}`);
    if (max != null && num > max) return fail(`${label} must be ≤ ${fmtNum(max)}`);
  }
  return null;
}

/** True when the rules object carries at least one active constraint. */
export function hasValidationRules(v: FieldValidation | null | undefined): boolean {
  if (!v) return false;
  return (
    (!!v.pattern && v.pattern.trim() !== "") ||
    v.min_length != null ||
    v.max_length != null ||
    v.min != null ||
    v.max != null
  );
}
